import { LitElement, html, nothing } from 'lit';
import { map } from 'lit/directives/map.js';
import { unsafeHTML } from 'lit/directives/unsafe-html.js';
import { BoilerplateModal } from './boilerplate_modal';

/**
 * Fetches published Workable jobs via Cloud Function POST endpoint.
 *
 * @fires careers-job-listings:loaded
 */
const JOBS_ENDPOINT =
  'https://australia-southeast1-foxtware-rocks.cloudfunctions.net/workableJobsGet';

class CareersJobModal extends BoilerplateModal {
  static get properties() {
    return {
      ...super.properties,
      job: { type: Object, state: true },
    };
  }

  constructor() {
    super();
    this.job = null;
  }

  toggleModal(e) {
    if (e?.detail?.job) {
      this.job = e.detail.job;
      this.title = String(e.detail.job.title || '');
    }
    super.toggleModal(e);
  }

  #renderApplyButton(applyUrl) {
    return applyUrl
      ? html`<a
          class="careers_job_modal__apply"
          href=${applyUrl}
          target="_blank"
          rel="noopener"
        >
          Apply now
        </a>`
      : nothing;
  }

  renderBody() {
    const job = this.job || this.detail?.job;
    if (!job) return nothing;

    const raw = job.full_description || job.description || '';
    const applyUrl = job.application_url || job.url || '';

    return html`
      ${this.#renderApplyButton(applyUrl)}
      <div class="careers_job_modal__body rte">
        ${raw ? unsafeHTML(raw) : html`<p>No description available.</p>`}
      </div>
      ${this.#renderApplyButton(applyUrl)}
    `;
  }
}

if (!customElements.get('careers-job-modal')) {
  customElements.define('careers-job-modal', CareersJobModal);
}

export class CareersJobListings extends LitElement {
  static get eventSourceId() {
    return 'careers-job-listings';
  }

  static properties = {
    heading: { type: String },
    summaryAfterJobs: { type: String },
    jobsWord: { type: String },
    viewRoleLabel: { type: String },
    loadMoreLabel: { type: String },
    pageSize: { type: Number },
    hiddenJobCodes: { type: String, attribute: 'hidden-job-codes' },
    jobs: { type: Array },
    loading: { type: Boolean },
    loadingMore: { type: Boolean },
    hasMore: { type: Boolean },
  };

  /** @type {AbortController | null} */
  #jobsAbort = null;

  /** Server-side paging cursor returned by the endpoint */
  #nextCursor = null;

  createRenderRoot() {
    return this;
  }

  constructor() {
    super();
    this.heading = 'JOB OPENINGS';
    this.summaryAfterJobs = 'IN ALL CATEGORIES';
    this.jobsWord = 'JOBS';
    this.viewRoleLabel = 'VIEW ROLE';
    this.loadMoreLabel = 'LOAD MORE';
    this.pageSize = 10;
    this.hiddenJobCodes = '';
    this.jobs = [];
    this.loading = false;
    this.loadingMore = false;
    this.hasMore = false;
  }

  /** @returns {Set<string>} */
  get #hiddenCodesSet() {
    if (!this.hiddenJobCodes) return new Set();
    return new Set(
      this.hiddenJobCodes
        .split(',')
        .map((c) => c.trim().toLowerCase())
        .filter(Boolean)
    );
  }

  /** @param {Record<string, unknown>} job */
  #hasSalaryRangeDescription(job) {
    const description = String(job.description || '');
    const fullDescription = String(job.full_description || '');
    return /salary\s*range/i.test(`${description} ${fullDescription}`);
  }

  /** @param {Record<string, unknown>[]} jobs */
  #filterAndSort(jobs) {
    const hidden = this.#hiddenCodesSet;
    const filtered = jobs.filter((job) => {
      const code = String(
        job.shortcode || job.code || job.id || ''
      ).toLowerCase();
      return (
        !(hidden.size && hidden.has(code)) &&
        !this.#hasSalaryRangeDescription(job)
      );
    });

    return filtered.slice().sort((a, b) => {
      const dateA = new Date(/** @type {string} */ (a.updated_at || 0));
      const dateB = new Date(/** @type {string} */ (b.updated_at || 0));
      return dateB - dateA;
    });
  }

  connectedCallback() {
    super.connectedCallback();
    this.#loadInitial();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#abortJobs();
    this.#nextCursor = null;
  }

  #abortJobs() {
    if (this.#jobsAbort) {
      this.#jobsAbort.abort();
      this.#jobsAbort = null;
    }
  }

  /** @param {unknown} raw */
  #formatEmploymentType(raw) {
    if (!raw) return '—';
    const key = String(raw).toLowerCase().replace(/-/g, '_');
    const labels = {
      full_time: 'Full-time',
      part_time: 'Part-time',
      contract: 'Contract',
      casual: 'Casual',
      temporary: 'Temporary',
      intern: 'Intern',
    };
    return labels[key] || String(raw).replace(/_/g, ' ');
  }

  /** @param {Record<string, unknown>} job */
  #pickLocation(job) {
    if (!job) return '—';
    const loc = job.location;
    if (loc && typeof loc === 'object') {
      const city = loc.city || '';
      const country = loc.country || '';
      if (city && country) return `${city}, ${country}`;
      return String(city || country || '—');
    }
    if (typeof loc === 'string' && loc.trim()) return loc;
    return '—';
  }

  /** @param {Record<string, unknown>} job */
  #jobIdentityKey(job) {
    if (job.id != null && String(job.id).trim()) return String(job.id).trim();
    if (job.slug != null && String(job.slug).trim())
      return String(job.slug).trim();
    return String(job.shortcode || job.code || '').trim();
  }

  #openJobModal(job) {
    document.dispatchEvent(
      new CustomEvent('careers-job-modal:open', {
        detail: { job, title: String(job.title || '') },
      })
    );
  }

  /**
   * @param {{ cursor?: string | null }} [opts]
   * @returns {Promise<{ jobs: Record<string, unknown>[]; nextCursor: string | null }>}
   */
  async #fetchJobs(opts = {}) {
    this.#abortJobs();
    this.#jobsAbort = new AbortController();
    const { signal } = this.#jobsAbort;

    const body = {
      options: {
        state: 'published',
        perPage: this.pageSize,
        includeFields: [
          'description',
          'full_description',
          'employment_type',
          'department',
          'industry',
          'updated_at',
          'published',
        ],
      },
    };
    if (opts.cursor) {
      body.options.cursor = opts.cursor;
    }

    const res = await fetch(JOBS_ENDPOINT, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
      signal,
    });

    if (!res.ok) {
      const text = await res.text();
      throw new Error(`Jobs endpoint ${res.status}: ${text.slice(0, 200)}`);
    }

    const data = await res.json();

    if (data.success === false) {
      throw new Error(
        data.error || data.message || 'API returned success: false'
      );
    }

    const jobs = Array.isArray(data.result)
      ? data.result
      : Array.isArray(data.jobs)
      ? data.jobs
      : Array.isArray(data)
      ? data
      : [];

    console.log(jobs);

    const nextCursor =
      data.nextCursor ?? data.next_cursor ?? data.cursor ?? null;

    return { jobs, nextCursor };
  }

  async #loadInitial() {
    this.loading = true;
    this.jobs = [];
    this.hasMore = false;
    this.#nextCursor = null;
    try {
      const { jobs, nextCursor } = await this.#fetchJobs();
      const visible = this.#filterAndSort(jobs);
      this.jobs = visible;
      this.#nextCursor = nextCursor;
      this.hasMore = !!nextCursor;
      this.dispatchEvent(
        new CustomEvent(`${CareersJobListings.eventSourceId}:loaded`, {
          bubbles: true,
          composed: true,
          detail: { count: visible.length },
        })
      );
    } catch (err) {
      if (err?.name === 'AbortError') return;
      console.error(err);
    } finally {
      this.loading = false;
    }
  }

  async #loadMore() {
    if (this.loadingMore || !this.hasMore) return;
    this.loadingMore = true;
    try {
      const { jobs, nextCursor } = await this.#fetchJobs({
        cursor: this.#nextCursor,
      });
      if (!jobs.length) {
        this.hasMore = false;
        return;
      }
      const known = new Set(
        this.jobs.map((j) => this.#jobIdentityKey(j)).filter(Boolean)
      );
      const fresh = this.#filterAndSort(
        jobs.filter((j) => {
          const key = this.#jobIdentityKey(j);
          return key && !known.has(key);
        })
      );
      if (!fresh.length) {
        this.#nextCursor = nextCursor;
        this.hasMore = !!nextCursor;
        return;
      }
      this.jobs = this.#filterAndSort([...this.jobs, ...fresh]);
      this.#nextCursor = nextCursor;
      this.hasMore = !!nextCursor;
    } catch (err) {
      if (err?.name === 'AbortError') return;
      console.error(err);
    } finally {
      this.loadingMore = false;
    }
  }

  /** @param {Record<string, unknown>} job */
  #renderJobRow(job) {
    return html`
      <button
        type="button"
        class="careers_job_openings__row"
        aria-label=${job.title || 'Job'}
        @click=${() => this.#openJobModal(job)}
      >
        <div
          class="careers_job_openings__cell careers_job_openings__cell--title"
        >
          ${job.title || '—'}
        </div>
        <div
          class="careers_job_openings__cell careers_job_openings__cell--type"
        >
          ${this.#formatEmploymentType(job.employment_type)}
        </div>
        <div
          class="careers_job_openings__cell careers_job_openings__cell--location"
        >
          ${this.#pickLocation(job)}
        </div>
        <div
          class="careers_job_openings__cell careers_job_openings__cell--cta"
          aria-hidden="true"
        >
          ${this.viewRoleLabel}
        </div>
      </button>
    `;
  }

  #renderSkeletonRows() {
    return Array.from(
      { length: 6 },
      (_, i) => html`
        <div
          class="careers_job_openings__row careers_job_openings__row--skeleton"
          aria-hidden="true"
        >
          <div
            class="careers_job_openings__cell careers_job_openings__cell--title"
          >
            <span
              class="careers_job_openings__skeleton_block"
              style="width:${55 + (i % 3) * 15}%"
            ></span>
          </div>
          <div
            class="careers_job_openings__cell careers_job_openings__cell--type"
          >
            <span
              class="careers_job_openings__skeleton_block"
              style="width:60%"
            ></span>
          </div>
          <div
            class="careers_job_openings__cell careers_job_openings__cell--location"
          >
            <span
              class="careers_job_openings__skeleton_block"
              style="width:45%"
            ></span>
          </div>
          <div
            class="careers_job_openings__cell careers_job_openings__cell--cta"
          >
            <span
              class="careers_job_openings__skeleton_block"
              style="width:70px"
            ></span>
          </div>
        </div>
      `
    );
  }

  render() {
    const count = this.jobs.length;
    const emptyLabel = 'No open roles right now.';

    return html`
      <div class="careers_job_openings__shell container">
        ${this.heading
          ? html`<div class="careers_job_openings__title_bar">
              <h2 class="careers_job_openings__title_heading">
                ${this.heading}
              </h2>
            </div>`
          : nothing}
        <p class="careers_job_openings__summary" aria-live="polite">
          ${this.loading
            ? html`<span
                class="careers_job_openings__summary_skeleton"
                aria-hidden="true"
              ></span>`
            : html`<span class="careers_job_openings__summary_lead"
                >${count}</span
              >`}
          <span class="careers_job_openings__summary_jobs"
            >${this.jobsWord}</span
          >
          <span class="careers_job_openings__summary_after"
            >${this.summaryAfterJobs}</span
          >
        </p>

        <div class="careers_job_openings__panel">
          ${this.loading
            ? this.#renderSkeletonRows()
            : count === 0
            ? html`<p class="careers_job_openings__empty">${emptyLabel}</p>`
            : map(this.jobs, (job) => this.#renderJobRow(job))}
        </div>

        ${this.hasMore && !this.loading
          ? html`<div class="careers_job_openings__load_more_wrap">
              <button
                type="button"
                class="careers_job_openings__load_more"
                ?disabled=${this.loadingMore}
                @click=${() => this.#loadMore()}
              >
                ${this.loadingMore ? '…' : this.loadMoreLabel}
              </button>
            </div>`
          : nothing}
      </div>
    `;
  }
}

if (!customElements.get('careers-job-listings')) {
  customElements.define('careers-job-listings', CareersJobListings);
}
